import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import ConsumerInquiryModal from '@/components/ConsumerInquiryModal';
import BuyerRmaRequestModal from '@/components/BuyerRmaRequestModal';
import { ArrowLeft, Package, Truck, Clock, CheckCircle2, XCircle, MapPin, RotateCcw, Undo2, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import api from '@/lib/api';
import SafeImg from '@/components/SafeImg';
import AddressSearch from '@/components/AddressSearch';
import ReturnToLiveButton from '@/components/ReturnToLiveButton';
import DepositInfo from '@/components/DepositInfo';
// STEP 3-OPS-C · REUSE existing trackingUrl helper (LOCK · no external API · official carrier URLs only).
// STEP 3-OPS-C-HOTFIX · use internal-only label (Carrier 실시간 상태 흉내 금지).
import { getTrackingAvailable, getTrackingUrl, getInternalShipmentLabel } from '@/lib/trackingUrl';

const won = (n) => `${(Number(n) || 0).toLocaleString('ko-KR')}원`;

// LIGHTPAY-CHECKOUT-RECOVERY-1 · 결제대기 PG 주문 재결제 (기존 /orders/:id/pay OrderPay 재사용 · 새 주문 없음)
const canPay = (o) => o?.status === 'payment_pending' && o?.payment?.method === 'pg';
const PAY_STATUS_LABEL = { paid: '결제완료', partially_refunded: '부분환불', refunded: '전액환불', pending: '결제대기' };

const PaymentSummary = ({ order }) => {
  const subs = (order.seller_orders || []).filter((s) => Number(s.paid_amount) > 0);
  if (!subs.length) return null;
  const paid = subs.reduce((t, s) => t + Number(s.paid_amount || 0), 0);
  const refunded = subs.reduce((t, s) => t + Number(s.refunded_amount || 0), 0);
  return (
    <div className="rounded-2xl border border-black/10 dark:border-white/10 p-4 mb-4" data-testid="order-payment-summary">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-bold">결제·환불 내역</p>
        <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-black/5 dark:bg-white/10" data-testid="order-payment-status">{PAY_STATUS_LABEL[order.payment_status] || PAY_STATUS_LABEL[order.payment?.status] || '결제대기'}</span>
      </div>
      <div className="grid grid-cols-3 gap-2 text-xs">
        <p className="text-gray-500">최초 결제<b className="block text-sm text-black dark:text-white" data-testid="order-paid-amount">{won(paid)}</b></p>
        <p className="text-gray-500">누적 환불<b className="block text-sm text-[#FF3B30]" data-testid="order-refunded-amount">{won(refunded)}</b></p>
        <p className="text-gray-500">남은 결제<b className="block text-sm text-black dark:text-white" data-testid="order-remaining-amount">{won(paid - refunded)}</b></p>
      </div>
      {subs.length > 1 && subs.map((s) => (
        <p key={s.seller_order_id} className="text-[11px] text-gray-500 mt-1" data-testid={`order-sub-pay-${s.seller_order_id}`}>
          {s.seller_name_snapshot} · {PAY_STATUS_LABEL[s.payment_status] || s.payment_status} · 결제 {won(s.paid_amount)} · 환불 {won(s.refunded_amount)}
        </p>
      ))}
    </div>
  );
};

const STATUS_TABS = [
  { key: '',                 label: '전체' },
  { key: 'payment_pending',  label: '결제대기' },
  { key: 'paid',             label: '결제완료' },
  { key: 'preparing',        label: '배송준비' },
  { key: 'shipping',         label: '배송중' },
  { key: 'delivered',        label: '배송완료' },
  { key: 'cancelled',        label: '취소/환불' },
];

const STATUS_STYLE = {
  payment_pending: { icon: Clock,        cls: 'text-amber-600 bg-amber-50 border-amber-200 dark:bg-amber-500/10 dark:border-amber-500/30' },
  paid:            { icon: CheckCircle2, cls: 'text-blue-600 bg-blue-50 border-blue-200 dark:bg-blue-500/10 dark:border-blue-500/30' },
  preparing:       { icon: Package,      cls: 'text-indigo-600 bg-indigo-50 border-indigo-200 dark:bg-indigo-500/10 dark:border-indigo-500/30' },
  shipping:        { icon: Truck,        cls: 'text-violet-600 bg-violet-50 border-violet-200 dark:bg-violet-500/10 dark:border-violet-500/30' },
  delivered:       { icon: CheckCircle2, cls: 'text-emerald-600 bg-emerald-50 border-emerald-200 dark:bg-emerald-500/10 dark:border-emerald-500/30' },
  confirmed:       { icon: CheckCircle2, cls: 'text-emerald-700 bg-emerald-50 border-emerald-200 dark:bg-emerald-500/10 dark:border-emerald-500/30' },
  cancelled:       { icon: XCircle,      cls: 'text-gray-500 bg-gray-100 border-gray-200 dark:bg-white/5 dark:border-white/10' },
  refund_requested:{ icon: XCircle,      cls: 'text-orange-600 bg-orange-50 border-orange-200 dark:bg-orange-500/10 dark:border-orange-500/30' },
  refunded:        { icon: XCircle,      cls: 'text-gray-500 bg-gray-100 border-gray-200 dark:bg-white/5 dark:border-white/10' },
};

const StatusBadge = ({ status, label }) => {
  const s = STATUS_STYLE[status] || STATUS_STYLE.payment_pending;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-bold ${s.cls}`} data-testid={`status-badge-${status}`}>
      <Icon size={11} /> {label || status}
    </span>
  );
};

const OrderCard = ({ order }) => (
  <Link to={`/orders/${order.order_id}`} className="block bg-white dark:bg-[#141414] rounded-2xl border border-black/10 dark:border-white/10 p-4 hover:border-[#FF3B30]/40 transition-colors" data-testid={`order-card-${order.order_id}`}>
    <div className="flex items-center justify-between mb-2">
      <div className="text-xs text-gray-500">
        <span className="font-bold">주문번호 {order.order_number}</span>
        <span className="mx-1.5">·</span>
        {order.created_at && format(new Date(order.created_at), 'yyyy.MM.dd HH:mm', { locale: ko })}
      </div>
      <StatusBadge status={order.status} label={order.status_label} />
    </div>
    <div className="space-y-2">
      {(order.seller_orders || []).slice(0, 3).map((so) => (
        <div key={so.seller_order_id} className="flex items-center gap-3 p-2 rounded-lg bg-black/[0.02] dark:bg-white/[0.03]">
          <div className="w-12 h-12 rounded-md overflow-hidden bg-gray-100 dark:bg-gray-800 shrink-0">
            <SafeImg src={(so.items || [])[0]?.image_snapshot} kind="product" alt="" className="w-full h-full object-cover" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] text-gray-500 truncate">{so.seller_name_snapshot}</p>
            <p className="font-bold text-sm truncate">
              {(so.items || [])[0]?.name_snapshot}
              {(so.items || []).length > 1 && <span className="text-gray-400 font-normal"> 외 {so.items.length - 1}건</span>}
            </p>
          </div>
          <StatusBadge status={so.status} label={so.status_label} />
        </div>
      ))}
    </div>
    <div className="mt-3 flex items-center justify-between text-sm">
      <span className="text-gray-500">총 {order.totals?.item_count || 0}개</span>
      <span className="font-black">{won(order.totals?.total)}</span>
    </div>
    {canPay(order) && (
      <span role="button" tabIndex={0} onClick={(e) => { e.preventDefault(); window.location.assign(`/orders/${order.order_id}/pay`); }}
        className="mt-3 block text-center py-2 rounded-full bg-[#FF3B30] hover:bg-[#D32F2F] text-white text-xs font-bold transition-colors"
        data-testid={`order-card-pay-${order.order_id}`}>결제하기</span>
    )}
  </Link>
);

const MyOrdersList = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState('');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async (st) => {
    setLoading(true);
    try {
      const params = st ? { status: st } : {};
      const res = await api.get('/orders', { params });
      setOrders(res.data || []);
    } catch (e) {
      if (e?.response?.status === 401) { toast.error('로그인이 필요합니다.'); navigate('/login'); }
    } finally { setLoading(false); }
  };
  useEffect(() => { load(status); }, [status]); // eslint-disable-line

  // "취소/환불" 필터는 서버 필터가 아니므로 클라이언트에서 결합 필터를 적용한다.
  const filteredOrders = useMemo(() => {
    if (status !== 'cancelled') return orders;
    return orders.filter((o) => ['cancelled', 'refund_requested', 'refunded'].includes(o.status));
  }, [orders, status]);

  return (
    <div className="min-h-screen bg-white dark:bg-[#0A0A0A] pb-16" data-testid="my-orders-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-4 sm:pt-8">
        <Link to="/dashboard" className="inline-flex items-center gap-1 text-xs sm:text-sm text-gray-500 hover:text-black dark:hover:text-white mb-4">
          <ArrowLeft size={14} /> 마이페이지
        </Link>
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>주문 내역</h1>
          <p className="text-sm text-gray-500 mt-1">주문 상태별로 확인할 수 있습니다.</p>
        </div>

        <div className="flex flex-wrap gap-2 mb-4" data-testid="orders-tabs">
          {STATUS_TABS.map((t) => (
            <button key={t.key || 'all'} type="button" onClick={() => setStatus(t.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${status === t.key ? 'bg-[#FF3B30] border-[#FF3B30] text-white' : 'border-black/10 dark:border-white/10 text-gray-600 dark:text-gray-300'}`}
              data-testid={`orders-tab-${t.key || 'all'}`}>
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-sm text-gray-400 py-6">불러오는 중…</div>
        ) : filteredOrders.length === 0 ? (
          <div className="py-16 text-center bg-black/[0.02] dark:bg-white/[0.03] rounded-2xl border border-dashed border-black/10 dark:border-white/10" data-testid="orders-empty">
            <Package className="mx-auto text-gray-300" size={28} />
            <p className="mt-3 text-sm font-bold">주문 내역이 없습니다.</p>
            <Link to="/" className="mt-3 inline-block text-xs font-bold text-[#FF3B30] hover:underline">홈으로 돌아가기 →</Link>
          </div>
        ) : (
          <div className="space-y-3" data-testid="orders-list">
            {filteredOrders.map((o) => <OrderCard key={o.order_id} order={o} />)}
          </div>
        )}
      </div>
    </div>
  );
};

const MyOrderDetail = () => {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const detailInputRef = useRef(null);

  const [askOpen, setAskOpen] = useState(false);
  const [eligibility, setEligibility] = useState(null);
  const [rmaModal, setRmaModal] = useState(null); // { type, subOrder }

  const load = async () => {
    try {
      const res = await api.get(`/orders/${orderId}`);
      setOrder(res.data);
      try {
        const e = await api.get(`/orders/${orderId}/rma/eligibility`);
        setEligibility(e.data);
      } catch { /* soft-fail */ }
    } catch (e) {
      if (e?.response?.status === 401) { navigate('/login'); return; }
      toast.error(e?.response?.data?.detail || '주문을 불러올 수 없습니다.');
      navigate('/orders');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [orderId]); // eslint-disable-line

  // Consumer direct cancel is ONLY allowed on payment_pending per PRD.
  const canDirectCancel = order && order.status === 'payment_pending';
  const canEditAddress = order && ['payment_pending', 'paid'].includes(order.status);

  const eligBySub = useMemo(() => {
    const m = {};
    (eligibility?.sub_orders || []).forEach((s) => { m[s.seller_order_id] = s; });
    return m;
  }, [eligibility]);

  const doCancel = async () => {
    if (!window.confirm('주문을 취소하시겠습니까? (결제대기 상태만 가능)')) return;
    setCancelling(true);
    try {
      await api.post(`/orders/${orderId}/cancel`, { reason: '구매자 취소' });
      toast.success('주문이 취소되었습니다.');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '취소에 실패했습니다.');
    } finally { setCancelling(false); }
  };

  const startEdit = () => {
    const addr = order.shipping_address_snapshot || {};
    setEditForm({ ...addr, buyer_message: order.buyer_message || '' });
    setEditing(true);
  };
  const applySearchResult = (r) => setEditForm((f) => ({
    ...f,
    postal_code: r.postal_code,
    road_address: r.road_address,
    jibun_address: r.jibun_address,
    base_address: r.base_address,
    extra_address: r.extra_address,
    address1: (r.base_address + (r.extra_address ? ' ' + r.extra_address : '')).trim(),
  }));
  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      await api.patch(`/orders/${orderId}/shipping-address`, {
        ...editForm,
        expected_updated_at: order?.updated_at,
      });
      toast.success('배송정보가 수정되었습니다.');
      setEditing(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || '수정 실패'); }
    finally { setSavingEdit(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-gray-400">불러오는 중…</div>;
  if (!order) return null;

  const addr = order.shipping_address_snapshot || {};
  const cls = "px-3 py-2 border border-black/10 dark:border-white/10 rounded-lg text-sm bg-transparent";

  return (
    <div className="min-h-screen bg-white dark:bg-[#0A0A0A] pb-16" data-testid="my-order-detail">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-4 sm:pt-8">
        <div className="flex items-center justify-between mb-4">
          <Link to="/orders" className="inline-flex items-center gap-1 text-xs sm:text-sm text-gray-500 hover:text-black dark:hover:text-white">
            <ArrowLeft size={14} /> 주문 내역
          </Link>
          {/* STEP 8-I-4A · 실제 주문 완료 화면(=상세)에도 반드시 방송 복귀 CTA */}
          <ReturnToLiveButton clearOnClick={true} />
        </div>

        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>주문 상세</h1>
            <p className="text-xs text-gray-500 mt-1">주문번호 <span className="font-bold" data-testid="order-number">{order.order_number}</span></p>
          </div>
          <StatusBadge status={order.status} label={order.status_label} />
        </div>

        <div className="flex gap-2 mb-4 flex-wrap">
          <button onClick={() => setAskOpen(true)} data-testid="buyer-ask-inquiry"
            className="flex-1 min-w-[130px] py-2 rounded-full text-xs font-bold border border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30]/5 inline-flex items-center justify-center gap-1">
            💬 문의하기
          </button>
          <Link to="/my/inquiries" className="flex-1 min-w-[130px] py-2 text-center rounded-full text-xs font-bold border border-black/10 dark:border-white/10" data-testid="buyer-my-inquiries-link">
            내 문의 보기
          </Link>
          <Link to="/my/rma" className="flex-1 min-w-[130px] py-2 text-center rounded-full text-xs font-bold border border-black/10 dark:border-white/10 inline-flex items-center justify-center gap-1" data-testid="buyer-my-rma-link">
            <RotateCcw size={11} /> 취소·교환·반품 내역
          </Link>
        </div>

        <div className="rounded-2xl border border-black/10 dark:border-white/10 p-4 mb-4" data-testid="order-address">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <MapPin size={14} className="text-[#FF3B30]" />
              <p className="text-sm font-bold">배송지</p>
            </div>
            {canEditAddress && !editing && (
              <button type="button" onClick={startEdit} className="text-xs font-bold text-[#FF3B30]" data-testid="buyer-edit-shipping">배송정보 수정</button>
            )}
          </div>
          {!canEditAddress && !editing && (
            <p className="text-[11px] text-gray-500 mb-1" data-testid="buyer-edit-shipping-blocked">이미 발송된 주문은 배송정보를 수정할 수 없습니다.</p>
          )}
          <ConsumerInquiryModal
            open={askOpen}
            onClose={() => setAskOpen(false)}
            scope="order"
            context={{
              order_id: order.order_id,
              verify_phone: addr.phone || order.buyer_snapshot?.phone,
              title: `${order.order_number} · ${(order.items||[])[0]?.product_name || ''}`,
            }}
            defaultCategory="shipping"
          />
          {editing ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="buyer-edit-shipping-form">
              <input placeholder="받는 사람" className={cls} value={editForm.recipient_name || ''} onChange={(e) => setEditForm({ ...editForm, recipient_name: e.target.value })} data-testid="buyer-edit-name" />
              <input placeholder="휴대전화" className={cls} value={editForm.phone || ''} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} data-testid="buyer-edit-phone" />
              <div className="flex gap-2">
                <input placeholder="우편번호" className={`${cls} flex-1`} readOnly value={editForm.postal_code || ''} data-testid="buyer-edit-postal" />
                <AddressSearch onComplete={applySearchResult} detailInputRef={detailInputRef} testId="buyer-edit-search" />
              </div>
              <input placeholder="기본 주소" className={cls} readOnly value={editForm.address1 || ''} data-testid="buyer-edit-line1" />
              <input ref={detailInputRef} placeholder="상세 주소" className={`${cls} sm:col-span-2`}
                value={editForm.address2 || ''}
                onChange={(e) => setEditForm({ ...editForm, address2: e.target.value, detail_address: e.target.value })}
                data-testid="buyer-edit-line2" />
              <input placeholder="배송 요청사항" className={`${cls} sm:col-span-2`}
                value={editForm.buyer_message || ''}
                onChange={(e) => setEditForm({ ...editForm, buyer_message: e.target.value })}
                data-testid="buyer-edit-message" />
              <div className="flex gap-2 sm:col-span-2">
                <button onClick={saveEdit} disabled={savingEdit} className="flex-1 py-2 rounded-full bg-[#FF3B30] text-white text-xs font-bold disabled:opacity-40" data-testid="buyer-edit-save">{savingEdit ? '저장 중…' : '저장'}</button>
                <button onClick={() => setEditing(false)} className="flex-1 py-2 rounded-full border border-black/10 dark:border-white/10 text-xs font-bold">취소</button>
              </div>
            </div>
          ) : (
            <>
              <p className="text-sm">{addr.recipient_name} · {addr.phone}</p>
              <p className="text-sm text-gray-500">[{addr.postal_code}] {addr.address1} {addr.address2}</p>
              {addr.memo && <p className="text-xs text-gray-500 mt-0.5">메모: {addr.memo}</p>}
              {order.buyer_message && <p className="text-xs text-gray-500 mt-1">요청사항: {order.buyer_message}</p>}
            </>
          )}
        </div>

        {canPay(order) && (
          <Link to={`/orders/${order.order_id}/pay`} className="mb-4 block text-center py-3 rounded-full bg-[#FF3B30] hover:bg-[#D32F2F] text-white text-sm font-bold transition-colors" data-testid="order-pay-btn">
            결제하기 · {won((order.seller_orders || []).filter((s) => s.status === 'payment_pending').reduce((t, s) => t + Number(s.totals?.total || 0), 0))}
          </Link>
        )}
        <PaymentSummary order={order} />

        <div className="space-y-4">
          {order.payment?.method === 'bank_transfer' && <DepositInfo orderId={order.order_id} />}
          {(order.seller_orders || []).map((so) => (
            <div key={so.seller_order_id} className="rounded-2xl border border-black/10 dark:border-white/10 p-4" data-testid={`sub-order-${so.seller_order_id}`}>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-bold">{so.seller_name_snapshot}</p>
                <StatusBadge status={so.status} label={so.status_label} />
              </div>
              <div className="space-y-2 mb-3">
                {(so.items || []).map((it, idx) => (
                  <div key={`${it.product_id}-${idx}`} className="flex items-center gap-3">
                    <Link to={`/product/${it.product_id}`} className="w-12 h-12 rounded-md overflow-hidden bg-gray-100 dark:bg-gray-800 shrink-0">
                      <SafeImg src={it.image_snapshot} kind="product" alt="" className="w-full h-full object-cover" />
                    </Link>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-sm truncate">{it.name_snapshot}</p>
                      {Object.keys(it.option || {}).length > 0 && (
                        <p className="text-[11px] text-gray-500 truncate">{Object.entries(it.option).map(([k, v]) => `${k}: ${v}`).join(' · ')}</p>
                      )}
                      <p className="text-xs mt-0.5">{won(it.unit_price)} × {it.quantity}</p>
                    </div>
                    <p className="font-black text-sm">{won(it.line_total)}</p>
                  </div>
                ))}
              </div>
              {so.shipment?.tracking_number && (() => {
                const sh = so.shipment || {};
                const status = getInternalShipmentLabel({ shipment: sh, orderStatus: so.status });
                const available = getTrackingAvailable(sh.courier, sh.tracking_number);
                const url = available ? getTrackingUrl(sh.courier, sh.tracking_number) : null;
                return (
                  <div className="p-2.5 rounded-lg bg-[#FF3B30]/5 border border-[#FF3B30]/20 text-xs" data-testid={`shipment-${so.seller_order_id}`}>
                    {so.combined_shipment_id && (
                      <p className="text-[10px] text-amber-700 mb-1">다른 주문과 함께 배송됩니다.</p>
                    )}
                    <div className="flex items-center justify-between gap-2 mb-0.5">
                      <p className="font-bold text-[#FF3B30]">배송조회</p>
                      {/* STEP 3-OPS-C · REUSE trackingUrl helper · official URL only · new tab */}
                      {available && url ? (
                        <a href={url} target="_blank" rel="noreferrer noopener"
                           className="inline-flex items-center gap-1 font-bold text-[#FF3B30] underline underline-offset-2 hover:text-[#e0332a]"
                           data-testid={`shipment-track-${so.seller_order_id}`}>
                          배송조회 ↗
                        </a>
                      ) : (
                        <span className="text-[10px] text-gray-400" data-testid={`shipment-track-pending-${so.seller_order_id}`}>배송조회 준비중</span>
                      )}
                    </div>
                    <p><span className="text-gray-500">택배사:</span> <span className="font-bold">{sh.courier}</span></p>
                    <p><span className="text-gray-500">송장번호:</span> <span className="font-mono font-bold">{sh.tracking_number}</span></p>
                    {status && <p className="mt-1"><span className="inline-block px-1.5 py-0.5 rounded-full bg-black/5 dark:bg-white/10 font-bold text-[10px]" data-testid={`shipment-track-status-${so.seller_order_id}`}>{status}</span></p>}
                    {sh.shipped_at && <p className="text-gray-500 mt-0.5">발송일: {format(new Date(sh.shipped_at), 'yyyy.MM.dd HH:mm', { locale: ko })}</p>}
                    {sh.delivered_at && <p className="text-emerald-600">배송완료: {format(new Date(sh.delivered_at), 'yyyy.MM.dd HH:mm', { locale: ko })}</p>}
                  </div>
                );
              })()}
              {/* Phase 2.7 · Per-item shipment status (multi-shipment support) */}
              {(so.shipments && so.shipments.length > 0) && (
                <div className="mt-2 space-y-2" data-testid={`shipments-list-${so.seller_order_id}`}>
                  {so.shipments.map((sh) => {
                    // STEP 3-OPS-C · Per-shipment tracking (REUSE trackingUrl · official URL only · no aggregation)
                    const status = getInternalShipmentLabel({ shipment: sh, orderStatus: so.status });
                    const available = getTrackingAvailable(sh.courier, sh.tracking_number);
                    const url = available ? getTrackingUrl(sh.courier, sh.tracking_number) : null;
                    return (
                      <div key={sh.shipment_id} className="p-2.5 rounded-lg bg-emerald-50/60 dark:bg-emerald-500/5 border border-emerald-200 dark:border-emerald-500/30 text-xs" data-testid={`buyer-shipment-${sh.shipment_id}`}>
                        <div className="flex items-center justify-between mb-1 gap-2 flex-wrap">
                          <p className="font-black text-emerald-700 dark:text-emerald-300">#{sh.shipment_no} · {sh.courier} · <span className="font-mono">{sh.tracking_number}</span></p>
                          <div className="flex items-center gap-2 flex-wrap">
                            {status && <span className="px-1.5 py-0.5 rounded-full bg-black/5 dark:bg-white/10 font-bold text-[10px]" data-testid={`buyer-shipment-status-${sh.shipment_id}`}>{status}</span>}
                            {available && url ? (
                              <a href={url} target="_blank" rel="noreferrer noopener"
                                 className="font-bold text-emerald-700 dark:text-emerald-300 underline underline-offset-2 hover:opacity-80"
                                 data-testid={`buyer-shipment-track-${sh.shipment_id}`}>
                                배송조회 ↗
                              </a>
                            ) : sh.tracking_number ? (
                              <span className="text-[10px] text-gray-400" data-testid={`buyer-shipment-track-pending-${sh.shipment_id}`}>배송조회 준비중</span>
                            ) : null}
                            {sh.shipped_at && <p className="text-[10px] text-emerald-700/70 dark:text-emerald-300/70">{format(new Date(sh.shipped_at), 'yyyy.MM.dd HH:mm', { locale: ko })}</p>}
                          </div>
                        </div>
                        <div className="space-y-0.5 mt-1">
                          {(sh.item_refs || []).map((r, i) => (
                            <p key={i} className="text-[11px] text-emerald-800/80 dark:text-emerald-200/80">
                              · {r.product_name_snapshot || r.product_id} × {r.quantity}
                            </p>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {/* Per-item pending/waiting badges */}
              {(so.items || []).some((it) => it.fulfillment_status && it.fulfillment_status !== 'shipped') && (
                <div className="mt-2 rounded-lg bg-amber-50/60 dark:bg-amber-500/5 border border-amber-200 dark:border-amber-500/30 p-2 text-[11px] space-y-1" data-testid={`item-pending-${so.seller_order_id}`}>
                  {(so.items || []).map((it, idx) => {
                    if (!it.fulfillment_status || it.fulfillment_status === 'shipped') return null;
                    const label = ({ ready: '출고준비', waiting_stock: '입고대기', out_of_stock: '품절', cancelled: '취소' })[it.fulfillment_status] || it.fulfillment_status;
                    const color = ({ waiting_stock: 'text-amber-700 dark:text-amber-300', ready: 'text-blue-700 dark:text-blue-300', out_of_stock: 'text-rose-700 dark:text-rose-300', cancelled: 'text-gray-500' })[it.fulfillment_status] || 'text-gray-700';
                    return (
                      <p key={`${it.product_id}-${idx}`} className={color} data-testid={`item-status-${so.seller_order_id}-${idx}`}>
                        · {it.name_snapshot} · <b>{label}</b>
                        {it.eta_date && ` (입고예정 ${it.eta_date})`}
                        {it.pending_qty > 0 && it.pending_qty < it.quantity && ` · 잔여 ${it.pending_qty}/${it.quantity}`}
                      </p>
                    );
                  })}
                </div>
              )}
              <div className="mt-3 pt-3 border-t border-black/5 dark:border-white/10 flex items-center justify-between text-xs">
                <span className="text-gray-500">상품 {won(so.totals?.subtotal)} + 배송비 {won(so.totals?.shipping_fee)}</span>
                <span className="font-black">{won(so.totals?.total)}</span>
              </div>

              <RmaActionsForSubOrder
                subOrder={so}
                eligibility={eligBySub[so.seller_order_id]}
                onRequest={(type) => setRmaModal({ type, subOrder: so })}
              />
            </div>
          ))}
        </div>

        <div className="mt-4 rounded-2xl bg-black/[0.03] dark:bg-white/[0.03] p-4 space-y-1.5" data-testid="order-totals">
          <div className="flex items-center justify-between text-sm"><span className="text-gray-500">상품 금액</span><span>{won(order.totals?.subtotal)}</span></div>
          <div className="flex items-center justify-between text-sm"><span className="text-gray-500">배송비</span><span>{won(order.totals?.shipping_fee)}</span></div>
          <div className="pt-2 mt-2 border-t border-black/5 dark:border-white/10 flex items-center justify-between">
            <span className="text-sm font-bold">총 결제 예정</span>
            <span className="text-xl font-black text-[#FF3B30]">{won(order.totals?.total)}</span>
          </div>
          <p className="text-[11px] text-gray-500 mt-1">결제수단: {order.payment?.method_display || '결제수단 미선택'}</p>
        </div>

        {canDirectCancel && (
          <button type="button" onClick={doCancel} disabled={cancelling}
            className="mt-4 w-full py-3 rounded-full border border-[#FF3B30]/40 text-[#FF3B30] text-sm font-bold hover:bg-[#FF3B30]/5 disabled:opacity-40"
            data-testid="order-cancel">
            {cancelling ? '취소 처리 중…' : '주문 취소'}
          </button>
        )}
        {!canDirectCancel && order && ['paid', 'preparing'].includes(order.status) && (
          <div className="mt-4 rounded-2xl border border-amber-200 dark:border-amber-500/30 bg-amber-50/60 dark:bg-amber-500/10 p-3 text-[11px] text-amber-700 dark:text-amber-300" data-testid="order-cancel-blocked">
            결제완료 이후에는 직접 취소가 불가합니다. 아래 판매자별 <b>[취소 문의]</b> 버튼으로 판매자에게 요청해주세요.
          </div>
        )}
      </div>

      {rmaModal && (
        <BuyerRmaRequestModal
          open={!!rmaModal}
          type={rmaModal.type}
          orderId={order.order_id}
          orderNumber={order.order_number}
          subOrder={rmaModal.subOrder}
          onClose={() => setRmaModal(null)}
          onCreated={() => { setRmaModal(null); load(); }}
        />
      )}
    </div>
  );
};

// Per-sub-order buttons rendered inside the sub-order card. Uses eligibility
// from GET /orders/{id}/rma/eligibility to gate exactly what the buyer can do.
const RmaActionsForSubOrder = ({ subOrder, eligibility, onRequest }) => {
  if (!eligibility) return null;
  const active = eligibility.active_rmas || [];
  const allowed = eligibility.allowed_types || [];
  if (active.length === 0 && allowed.length === 0) return null;

  return (
    <div className="mt-3 pt-3 border-t border-black/5 dark:border-white/10 space-y-2" data-testid={`rma-actions-${subOrder.seller_order_id}`}>
      {active.length > 0 && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 dark:bg-amber-500/10 dark:border-amber-500/30 p-2 text-[11px]" data-testid={`rma-active-${subOrder.seller_order_id}`}>
          <p className="font-black text-amber-700 dark:text-amber-300 mb-1">진행 중인 요청</p>
          {active.map((a) => (
            <p key={a.rma_id} className="text-amber-700 dark:text-amber-300">
              {a.type_label} · {a.status_label}
              {' · '}
              <Link to="/my/rma" className="underline font-bold">상세 보기</Link>
            </p>
          ))}
        </div>
      )}
      {active.length === 0 && allowed.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {allowed.includes('cancel') && (
            <button onClick={() => onRequest('cancel')} className="flex-1 min-w-[110px] py-2 rounded-full text-xs font-bold border border-red-200 text-red-700 hover:bg-red-50 dark:border-red-500/30 dark:text-red-300 dark:hover:bg-red-500/10 inline-flex items-center justify-center gap-1" data-testid={`buyer-request-cancel-${subOrder.seller_order_id}`}>
              <XCircle size={12} /> 취소 문의
            </button>
          )}
          {allowed.includes('exchange') && (
            <button onClick={() => onRequest('exchange')} className="flex-1 min-w-[110px] py-2 rounded-full text-xs font-bold border border-orange-200 text-orange-700 hover:bg-orange-50 dark:border-orange-500/30 dark:text-orange-300 dark:hover:bg-orange-500/10 inline-flex items-center justify-center gap-1" data-testid={`buyer-request-exchange-${subOrder.seller_order_id}`}>
              <RotateCcw size={12} /> 교환 신청
            </button>
          )}
          {allowed.includes('return') && (
            <button onClick={() => onRequest('return')} className="flex-1 min-w-[110px] py-2 rounded-full text-xs font-bold border border-purple-200 text-purple-700 hover:bg-purple-50 dark:border-purple-500/30 dark:text-purple-300 dark:hover:bg-purple-500/10 inline-flex items-center justify-center gap-1" data-testid={`buyer-request-return-${subOrder.seller_order_id}`}>
              <Undo2 size={12} /> 반품 신청
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export { MyOrdersList, MyOrderDetail };
export default MyOrdersList;